import { type ApolloClient } from '@apollo/client';
import { isEmpty, isObject } from 'lodash/fp';
import { type LocalVariantSpecsFragment } from '../../../api/fragments/LocalVariantSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteVariantAssetDocument,
    type DeleteVariantAssetMutation,
    type DeleteVariantAssetMutationVariables,
} from '../../../api/mutations/deleteVariantAsset';
import {
    UploadVariantAssetDocument,
    type UploadVariantAssetMutation,
    type UploadVariantAssetMutationVariables,
} from '../../../api/mutations/uploadVariantAsset';
import { type UploadedFileWithPreview, VariantAsset } from '../../../api/types';
import { type FormValues } from '../AddVehiclePage/AddVariantPage/types';

export const imagesUpdates = (
    variantId: string,
    apolloClient: ApolloClient<object>,
    initialValues?: LocalVariantSpecsFragment,
    images?: FormValues['images']
) => {
    const uploads = [];
    const deletes = [];

    const imagesToUploadHaveIds = images.filter(
        (image: File | UploadFileWithPreviewFormDataFragment) => !(image instanceof File)
    ) as UploadedFileWithPreview[];
    const imageIdsToDelete = [];
    (initialValues?.images || []).forEach(image => {
        if (image.id && !imagesToUploadHaveIds.find(imageToUpload => imageToUpload?.id === image.id)) {
            imageIdsToDelete.push(image.id);
        }
    });

    images.forEach(image => {
        if (image instanceof File) {
            uploads.push(
                apolloClient.mutate<UploadVariantAssetMutation, UploadVariantAssetMutationVariables>({
                    mutation: UploadVariantAssetDocument,
                    variables: { variantId, type: VariantAsset.Picture, upload: image },
                })
            );
        } else {
            uploads.push(image);
        }
    });

    imageIdsToDelete.forEach(imageId => {
        deletes.push(
            apolloClient.mutate<DeleteVariantAssetMutation, DeleteVariantAssetMutationVariables>({
                mutation: DeleteVariantAssetDocument,
                variables: { variantId, uploadId: imageId, type: VariantAsset.Picture },
            })
        );
    });

    return {
        uploads,
        deletes,
    };
};

export const brochureUpdate = (
    variantId: string,
    apolloClient: ApolloClient<object>,
    initialValues?: LocalVariantSpecsFragment,
    brochure?: FormValues['brochure']
) => {
    const uploads = [];
    if (brochure instanceof File) {
        uploads.push(
            apolloClient.mutate<UploadVariantAssetMutation, UploadVariantAssetMutationVariables>({
                mutation: UploadVariantAssetDocument,
                variables: { variantId, type: VariantAsset.Brochure, upload: brochure },
            })
        );
    } else if (brochure === null && isObject(initialValues.brochure) && !isEmpty(initialValues.brochure)) {
        // delete existing file
        uploads.push(
            apolloClient.mutate<DeleteVariantAssetMutation, DeleteVariantAssetMutationVariables>({
                mutation: DeleteVariantAssetDocument,
                variables: { variantId, uploadId: initialValues.brochure.id, type: VariantAsset.Brochure },
            })
        );
    }

    return uploads;
};

export const extractVariantIdFromResponse = (result: any[]) => {
    if (result?.length > 0) {
        const variantId = result?.[result.length - 1]?.data?.localVariant?.id ?? null;

        return variantId;
    }

    return null;
};

export const extractVariantIdFromImageUploadResponse = (uploadedImages: any[]) => {
    const newFileUploaded = uploadedImages.find(
        x => x.data?.uploadedData !== undefined && x.data.uploadedData.variantId !== undefined
    );
    if (newFileUploaded) {
        const {
            data: {
                uploadedData: { variantId: updatedVaraintId },
            },
        } = newFileUploaded;

        return updatedVaraintId;
    }

    return null;
};

export const hasOrderingChange = (initialValues: LocalVariantSpecsFragment, values: FormValues) => {
    let i = 0;
    const hasOrderingChange = initialValues.images?.some(image => {
        if (image && i < values.images?.length && !(values.images[i] instanceof File)) {
            if (
                image.__typename === 'UploadedFileWithPreview' &&
                (values.images[i] as UploadedFileWithPreview).__typename !== 'UploadedFileWithPreview'
            ) {
                return true;
            }

            if (image.id !== (values.images[i] as UploadedFileWithPreview).id) {
                return true;
            }
        }
        i++;

        return false;
    });

    return hasOrderingChange;
};
